import React from 'react'
import { css } from 'emotion'
import { Spacer, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import tinycolor from 'tinycolor2'
import { CheckmarkCircledIcon, InitiativeDiscussionIcon, LockGlyph24Icon,
   QuestionGlyph16Icon, ViewGlyph12Icon } from 'assets/icons'
import InitiativePrivacyBadge from 'src/apps/Initiative/components/PrivacyBadge'

const DiscussionListItemRow = ({
   addedBy,
   backgroundColor,
   borderStyle,
   dateLastUpdated,
   discussionFormat,
   discussionIsClosed,
   // This props is used to force the presentation of this component
   // to be in the narrow/mobile context even when the screen size is large,
   // for example, in a narrow column
   forceCompactDisplay,
   hasUnreadEntries,
   linkTo,
   name,
   numThreadEntries,
   numViews,
   pinned,
   privacy
}) => (
   <Link to={linkTo}>
      <View
         display="flex"
         justifyContent="space-between"
         alignItems="center"
         borderTop={borderStyle}
         padding={forceCompactDisplay ? '20px 0px' : '20px 10px'}
         className={css`
            background-color: ${hasUnreadEntries ? 'rgba(56, 170, 242, 0.05)' : backgroundColor};

            &:hover {
               background-color: rgba(56, 170, 242, 0.03);
            }
         `}>
         {hasUnreadEntries && (
            <View
               flex="0 0 auto"
               height="10px"
               width="10px"
               margin="0px 25px 0px 10px"
               borderRadius="50%"
               backgroundColor={hasUnreadEntries && '#3893f2'}
            />
         )}
         {!hasUnreadEntries && (
            <View
               display="flex"
               justifyContent="center"
               width="50px"
               padding="0px 25px 0px 5px"
               flex="0 0 auto">
               {discussionFormat === 'discussion' && !discussionIsClosed && (
                  <View opacity={(30 + (numThreadEntries * 20)) / 100}>
                     <InitiativeDiscussionIcon
                        width="14px"
                        height="14px"
                        color={privacy === 'draft'
                           ? '#aaa'
                           : tinycolor('hsl(220, 10%, 50%)').saturate(numThreadEntries).toString()
                        }
                     />
                  </View>
               )}
               {discussionFormat === 'discussion' && discussionIsClosed && (
                  <View paddingLeft="2px">
                     <LockGlyph24Icon
                        width="18px"
                        height="18px"
                        color="#aaa"
                     />
                  </View>
               )}
               {discussionFormat === 'qa' && !discussionIsClosed && (
                  <QuestionGlyph16Icon
                     width="16px"
                     height="16px"
                     color={privacy === 'draft' ? '#aaa' : '#894ae3'}
                  />
               )}
               {discussionFormat === 'qa' && discussionIsClosed && (
                  <CheckmarkCircledIcon
                     width="16px"
                     height="16px"
                     color="#2fb949"
                  />
               )}
            </View>
         )}
         <View
            display="flex"
            flex="1 1 auto"
            flexFlow={forceCompactDisplay ? 'column' : 'column[a-b] row[c-f]'}>
            <View flex="1 1 auto" paddingRight={!forceCompactDisplay && '60px[c-f]'}>
               <View display="flex" alignItems="center">
                  <Text size="2" weight="medium" color="#222">
                     {name}
                  </Text>
                  {pinned && (
                     <View
                        display="inline-flex"
                        alignItems="center"
                        height="15px"
                        backgroundColor="#fee384"
                        padding="0px 5px"
                        borderRadius="3px"
                        marginLeft="10px">
                        <Text size="0.7" weight="bold" transform="uppercase" color="#000">
                           Pinned
                        </Text>
                     </View>
                  )}
               </View>
               <Spacer size="0[a-b] 0.5[c-f]" />
               <View display="block[a-b] flex[c-f]">
                  <InitiativePrivacyBadge privacy={privacy} />
                  <Text size="1" weight="normal" color="#888">
                     {discussionIsClosed && (
                        <View
                           display={forceCompactDisplay ? 'none' : 'none[a-b] inline-block[c-f]'}
                           marginRight="3px"
                           style={{
                              fontWeight: '500',
                              color: discussionFormat === 'qa' ? '#2fb949' : '#666'
                           }}>
                           {discussionFormat === 'qa'
                              ? 'Answered  ·  '
                              : 'Closed  ·  '
                           }
                        </View>
                     )}
                     Posted by
                     {` ${addedBy} `}
                     &#183;
                     {` Updated ${dateLastUpdated}`}
                  </Text>
               </View>
            </View>
            <View
               display="flex"
               alignItems="center"
               flex="0 0 auto"
               paddingRight="15px">
               {numThreadEntries > 0 && (
                  <View display="flex" alignItems="center" marginLeft="15px">
                     <View marginRight="5px">
                        <InitiativeDiscussionIcon
                           width="12px"
                           height="12px"
                           color="#aaa"
                        />
                     </View>
                     <Text size="1" color="#aaa">
                        {`${pluralize('Replies', numThreadEntries, true)} `}
                     </Text>
                  </View>
               )}
               {numViews > 0 && (
                  <View display="flex" alignItems="center" marginLeft="15px">
                     <View margin="0px 5px 0px 0px">
                        <ViewGlyph12Icon
                           width="12px"
                           height="12px"
                           color="#aaa"
                        />
                     </View>
                     <Text size="1" color="#aaa">
                        {pluralize('Views', numViews, true)}
                     </Text>
                  </View>
               )}
            </View>
         </View>
      </View>
   </Link>
)

DiscussionListItemRow.propTypes = {
   addedBy: PropTypes.string.isRequired,
   backgroundColor: PropTypes.string,
   borderStyle: PropTypes.string,
   dateLastUpdated: PropTypes.string.isRequired,
   discussionFormat: PropTypes.string.isRequired,
   discussionIsClosed: PropTypes.bool.isRequired,
   forceCompactDisplay: PropTypes.bool,
   hasUnreadEntries: PropTypes.bool,
   linkTo: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string
   ]).isRequired,
   name: PropTypes.string.isRequired,
   numThreadEntries: PropTypes.number.isRequired,
   numViews: PropTypes.number.isRequired,
   pinned: PropTypes.bool.isRequired,
   privacy: PropTypes.string.isRequired
}

DiscussionListItemRow.defaultProps = {
   backgroundColor: undefined,
   borderStyle: '1px solid #ddd',
   forceCompactDisplay: false,
   hasUnreadEntries: false
}

export default DiscussionListItemRow
